import PublicLayout from "@/components/layout/PublicLayout";
import { useEffect, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { fetchArticle } from "@/lib/api";
import {
    ArrowUpRight,
    Clock,
    Quote,
    AlertTriangle,
    MessageCircle,
    Wrench,
    Lightbulb,
    ShieldCheck,
    Share2,
    Copy,
} from "lucide-react";
import InteractiveMiniTool from "@/components/learn/InteractiveMiniTool";
import NewsletterInlineCapture from "@/components/learn/NewsletterInlineCapture";
import ParallaxImage from "@/components/motion/ParallaxImage";
import useExperimentVariant, { fireExperimentEvent } from "@/hooks/useExperimentVariant";

// ---------- Minimal markdown (same as before, kept for content_engine body) ----------
function renderMarkdown(md) {
    const lines = (md || "").split("\n");
    const out = [];
    let para = [];
    let listBuf = null;
    const flushPara = () => {
        if (para.length) {
            out.push({ type: "p", text: para.join(" ") });
            para = [];
        }
    };
    const flushList = () => {
        if (listBuf) {
            out.push({ type: "ul", items: listBuf });
            listBuf = null;
        }
    };
    for (const raw of lines) {
        const line = raw.trim();
        if (line.startsWith("## ")) {
            flushPara();
            flushList();
            out.push({ type: "h2", text: line.slice(3) });
        } else if (line.startsWith("### ")) {
            flushPara();
            flushList();
            out.push({ type: "h3", text: line.slice(4) });
        } else if (line.startsWith("- ")) {
            flushPara();
            listBuf = listBuf || [];
            listBuf.push(line.slice(2));
        } else if (line === "---") {
            flushPara();
            flushList();
            out.push({ type: "hr" });
        } else if (line === "") {
            flushPara();
            flushList();
        } else {
            para.push(line);
        }
    }
    flushPara();
    flushList();
    return out;
}

function inlineMd(text) {
    const parts = [];
    let i = 0;
    let buf = "";
    while (i < (text || "").length) {
        if (text[i] === "*" && text[i + 1] === "*") {
            const end = text.indexOf("**", i + 2);
            if (end > -1) {
                if (buf) parts.push(buf);
                buf = "";
                parts.push(<strong key={i}>{text.slice(i + 2, end)}</strong>);
                i = end + 2;
                continue;
            }
        }
        if (text[i] === "*") {
            const end = text.indexOf("*", i + 1);
            if (end > -1) {
                if (buf) parts.push(buf);
                buf = "";
                parts.push(
                    <em key={i} className="font-italic-accent text-bl-primary">
                        {text.slice(i + 1, end)}
                    </em>,
                );
                i = end + 1;
                continue;
            }
        }
        buf += text[i++];
    }
    if (buf) parts.push(buf);
    return parts;
}

// ---------- Structured field renderers ----------
function KeyTakeaways({ items }) {
    if (!items?.length) return null;
    return (
        <div
            className="mt-8 p-5 md:p-6 rounded-lg border-l-4 border-bl-primary bg-bl-bg"
            data-testid="learn-takeaways"
        >
            <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted mb-2">
                Quick answer
            </div>
            <ul className="space-y-2">
                {items.map((it, i) => (
                    <li key={i} className="text-bl flex gap-2">
                        <span className="text-bl-primary mt-1">·</span>
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function InfographicInline({ slug, altText }) {
    return (
        <figure className="my-10" data-testid="learn-infographic">
            <img
                src={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/learn/articles/${slug}/infographic.svg`}
                alt={altText || "Infographic"}
                className="w-full rounded-lg border border-bl/40 max-w-2xl mx-auto"
                loading="lazy"
            />
            {altText && (
                <figcaption className="text-center mt-2 text-xs text-bl-muted">
                    {altText}
                </figcaption>
            )}
        </figure>
    );
}

function InspectionWordingExplained({ items }) {
    if (!items?.length) return null;
    return (
        <section className="mt-12" data-testid="learn-inspection-wording">
            <h2 className="font-display font-semibold text-2xl md:text-3xl text-bl mb-4">
                Inspection wording, explained
            </h2>
            <div className="space-y-4">
                {items.map((item, i) => (
                    <div
                        key={i}
                        className="bl-card p-5 border-l-4 border-[var(--bl-stone-soft)]"
                    >
                        <div className="flex items-start gap-3">
                            <Quote className="h-4 w-4 text-bl-muted mt-1 shrink-0" />
                            <div>
                                <div className="font-italic-accent text-bl-primary italic mb-1">
                                    "{item.phrase}"
                                </div>
                                <p className="text-bl">
                                    <span className="text-bl-muted">What this usually means: </span>
                                    {item.what_it_means}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function SeverityMeter({ meter }) {
    if (!meter) return null;
    const levels = [
        { key: "low", color: "bg-[var(--bl-green)]" },
        { key: "moderate", color: "bg-[var(--bl-amber)]" },
        { key: "high", color: "bg-[var(--bl-rust)]" },
    ];
    return (
        <section className="mt-12" data-testid="learn-severity-meter">
            <h2 className="font-display font-semibold text-2xl md:text-3xl text-bl mb-4">
                Severity at a glance
            </h2>
            <div className="grid sm:grid-cols-3 gap-4">
                {levels.map((lvl) => {
                    const entry = meter[lvl.key];
                    if (!entry) return null;
                    return (
                        <div
                            key={lvl.key}
                            className="bl-card overflow-hidden"
                            data-testid={`severity-${lvl.key}`}
                        >
                            <div className={`h-1.5 ${lvl.color}`} />
                            <div className="p-4">
                                <div className="font-display font-semibold text-lg text-bl">
                                    {entry.label}
                                </div>
                                <p className="text-sm text-bl-muted mt-1">
                                    {entry.description}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function QuestionList({ heading, icon: Icon, items, testid }) {
    if (!items?.length) return null;
    return (
        <div className="bl-card p-5" data-testid={testid}>
            <div className="flex items-center gap-2 mb-3">
                <Icon className="h-4 w-4 text-bl-primary" />
                <h3 className="font-display font-semibold text-lg text-bl">{heading}</h3>
            </div>
            <ul className="space-y-2">
                {items.map((q, i) => (
                    <li key={i} className="text-sm text-bl flex gap-2">
                        <span className="text-bl-muted mt-0.5">{i + 1}.</span>
                        <span>{q}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function FAQAccordion({ items }) {
    if (!items?.length) return null;
    return (
        <section className="mt-12" data-testid="learn-faq">
            <h2 className="font-display font-semibold text-2xl md:text-3xl text-bl mb-4">
                Frequently asked
            </h2>
            <div className="space-y-2">
                {items.map((f, i) => (
                    <details
                        key={i}
                        className="bl-card p-4 group"
                        data-testid={`faq-${i}`}
                    >
                        <summary className="cursor-pointer font-medium text-bl flex items-center justify-between">
                            <span>{f.q}</span>
                            <span className="text-bl-muted group-open:rotate-45 transition-transform">
                                +
                            </span>
                        </summary>
                        <p className="mt-3 text-bl leading-relaxed">{f.a}</p>
                    </details>
                ))}
            </div>
        </section>
    );
}

function MainCtaBlock({ block, articleSlug, variant }) {
    const headline = block?.headline || "Have an actual inspection report?";
    const body =
        block?.body ||
        "Upload it and receive a report-specific analysis with prioritized concerns, repair exposure planning, and negotiation guidance.";
    const primary = block?.primary_button || {
        label: "Upload Inspection",
        href: "/upload",
    };
    const secondary = block?.secondary_button || {
        label: "View Sample Report",
        href: "/sample-report",
    };
    const trust = block?.trust_row || [
        "Private upload",
        "Delete reports anytime",
        "Read-only sharing",
        "No contractor spam",
    ];
    const onCtaClick = (which) => {
        if (variant) {
            fireExperimentEvent(
                variant.experiment_id, variant.variant_id,
                "upload_cta_clicked",
                null,
                { slug: articleSlug, which },
            );
        }
        if (typeof window !== "undefined" && window.posthog?.capture) {
            window.posthog.capture("article_upload_cta_clicked", {
                slug: articleSlug,
                which,
                ...(variant ? { variant_id: variant.variant_id } : {}),
            });
        }
    };
    return (
        <div
            className="mt-12 bl-card p-7 md:p-9"
            data-testid="learn-main-cta"
        >
            <h3 className="font-display font-semibold text-2xl md:text-3xl text-bl">
                {headline}
            </h3>
            <p className="mt-3 text-bl-muted">{body}</p>
            <div className="mt-5 flex gap-3 flex-wrap">
                <Link
                    to={primary.href || "/upload"}
                    onClick={() => onCtaClick("primary")}
                    className="bl-btn-primary gap-1.5"
                    data-testid="article-cta-upload"
                >
                    {primary.label}
                    <ArrowUpRight className="w-4 h-4" />
                </Link>
                {secondary && (
                    <Link
                        to={secondary.href || "/sample-report"}
                        onClick={() => onCtaClick("secondary")}
                        className="bl-btn-ghost"
                        data-testid="article-cta-secondary"
                    >
                        {secondary.label}
                    </Link>
                )}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-bl-muted">
                {trust.map((t) => (
                    <span key={t} className="flex items-center gap-1">
                        <ShieldCheck className="h-3 w-3" /> {t}
                    </span>
                ))}
            </div>
        </div>
    );
}

function InternalLinksBlock({ links }) {
    if (!links?.length) return null;
    return (
        <section className="mt-12" data-testid="learn-internal-links">
            <div className="bl-eyebrow">You might also want</div>
            <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                {links.map((l, i) => (
                    <li key={i}>
                        <Link
                            to={l.url || "#"}
                            className="bl-card p-3 block hover:bg-bl-bg/60"
                            data-testid={`internal-link-${i}`}
                        >
                            <span className="text-bl-primary">{l.anchor}</span>
                            {l.rationale && (
                                <div className="text-xs text-bl-muted mt-0.5">
                                    {l.rationale}
                                </div>
                            )}
                        </Link>
                    </li>
                ))}
            </ul>
        </section>
    );
}

function ShareButtons({ slug, title }) {
    const fullUrl = typeof window !== "undefined"
        ? `${window.location.origin}/learn/${slug}`
        : `/learn/${slug}`;
    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(fullUrl);
            toast.success("Link copied", { description: fullUrl });
        } catch {
            toast.error("Could not copy link");
        }
    };
    const links = [
        { label: "Pinterest", url: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(fullUrl)}&description=${encodeURIComponent(title || "")}` },
        { label: "Facebook", url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}` },
        { label: "X", url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(fullUrl)}&text=${encodeURIComponent(title || "")}` },
        { label: "LinkedIn", url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}` },
        { label: "Email", url: `mailto:?subject=${encodeURIComponent(title || "")}&body=${encodeURIComponent(fullUrl)}` },
    ];
    return (
        <div className="mt-12 flex items-center gap-2 flex-wrap" data-testid="learn-share">
            <Share2 className="h-4 w-4 text-bl-muted" />
            <span className="text-xs text-bl-muted mr-2">Share</span>
            <button
                type="button"
                onClick={copyLink}
                className="bl-btn-ghost text-xs gap-1"
                data-testid="learn-share-copy"
            >
                <Copy className="h-3 w-3" /> Copy link
            </button>
            {links.map((l) => (
                <a
                    key={l.label}
                    href={l.url}
                    target="_blank"
                    rel="noreferrer"
                    className="bl-btn-ghost text-xs"
                    data-testid={`learn-share-${l.label.toLowerCase()}`}
                >
                    {l.label}
                </a>
            ))}
        </div>
    );
}

// ---------- Main page ----------
export default function LearnArticle() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    // A/B variant for learn article CTAs (Phase 3 wiring).
    const { experiments } = useExperimentVariant("learn_article");
    const ctaVariant = experiments.find((e) => (e.config?.target || "learn_cta") === "learn_cta");

    // ?preview=email — open the production newsletter render of this article
    // (admin QA shortcut). Server returns the exact HTML we'd send via Resend,
    // inlined logo + hero + greeting. Renders inside an iframe so the email's
    // <html><body> shell stays isolated from the React app's chrome.
    const previewMode = searchParams.get("preview") === "email";
    const previewFirstName = searchParams.get("first_name") || "Andrew";

    useEffect(() => {
        if (previewMode) return; // skip data fetch in preview mode
        setLoading(true);
        fetchArticle(slug)
            .then(setData)
            .finally(() => setLoading(false));
    }, [slug, previewMode]);

    // Inject the JSON-LD schemas (if present) into the document head once
    // the article loads. This is critical for SERP rich-results.
    useEffect(() => {
        const article = data?.article;
        if (!article?.schema_json) return;
        const inserted = [];
        try {
            Object.entries(article.schema_json).forEach(([key, blob]) => {
                const el = document.createElement("script");
                el.type = "application/ld+json";
                el.setAttribute("data-bl-schema", key);
                el.text = JSON.stringify(blob);
                document.head.appendChild(el);
                inserted.push(el);
            });
        } catch {
            /* no-op */
        }
        return () => inserted.forEach((el) => el.remove());
    }, [data]);

    // SEO consolidation for legacy slug redirects: tell crawlers the new
    // canonical URL + ask them to deindex the legacy path while still
    // following the redirect. Modern Googlebot executes the <Navigate />
    // below; conservative crawlers still pick up the canonical signal.
    useEffect(() => {
        if (data?.status !== "redirect" || !data?.redirect_to) return;
        const newUrl = `${window.location.origin}/learn/${data.redirect_to}`;
        const canonical = document.createElement("link");
        canonical.rel = "canonical";
        canonical.href = newUrl;
        canonical.setAttribute("data-bl-redirect-canonical", "1");
        document.head.appendChild(canonical);
        const robots = document.createElement("meta");
        robots.name = "robots";
        robots.content = "noindex, follow";
        robots.setAttribute("data-bl-redirect-robots", "1");
        document.head.appendChild(robots);
        // Conservative <meta refresh> fallback for legacy crawlers without JS
        const refresh = document.createElement("meta");
        refresh.httpEquiv = "refresh";
        refresh.content = `0; url=${newUrl}`;
        refresh.setAttribute("data-bl-redirect-refresh", "1");
        document.head.appendChild(refresh);
        return () => {
            canonical.remove();
            robots.remove();
            refresh.remove();
        };
    }, [data]);

    if (previewMode) {
        const previewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/learn/articles/${slug}/email-preview?first_name=${encodeURIComponent(previewFirstName)}`;
        return (
            <div className="min-h-screen bg-[#F7F5F1] flex flex-col" data-testid="learn-email-preview-shell">
                <div className="bg-[#1F2428] text-white px-6 py-3 flex items-center justify-between text-sm">
                    <div>
                        <span className="font-semibold">Email preview</span>
                        <span className="ml-3 text-bl-muted">Newsletter render of /learn/{slug} · first name: {previewFirstName}</span>
                    </div>
                    <Link to={`/learn/${slug}`} className="underline text-white" data-testid="exit-email-preview">
                        ← Exit preview
                    </Link>
                </div>
                <iframe
                    title="Email preview"
                    src={previewUrl}
                    className="flex-1 w-full border-0"
                    style={{ minHeight: "calc(100vh - 48px)" }}
                    data-testid="learn-email-preview-iframe"
                />
            </div>
        );
    }

    // Legacy-slug redirect: API tells us this slug has moved to a new
    // SEO-optimized URL. SPA-navigate (replace history so back-button doesn't
    // loop) — no 404 flash for the user.
    if (data?.status === "redirect" && data?.redirect_to) {
        return <Navigate to={`/learn/${data.redirect_to}`} replace />;
    }

    if (loading) {
        return (
            <PublicLayout>
                <div className="bl-container py-24 text-center text-bl-muted">
                    <div className="bl-spinner mx-auto mb-4" /> Loading…
                </div>
            </PublicLayout>
        );
    }

    if (!data?.article) {
        const suggestions = data?.suggestions || [];
        return (
            <PublicLayout>
                <div className="bl-container py-24 max-w-3xl">
                    <h1 className="font-display text-3xl text-bl">
                        We couldn't find that article
                    </h1>
                    <p className="mt-3 text-bl-muted leading-relaxed">
                        The URL <code className="px-1.5 py-0.5 bg-bl-bg rounded text-sm">/learn/{slug}</code>{" "}
                        doesn't match a published article — it may have been renamed or removed.
                    </p>

                    {suggestions.length > 0 && (
                        <div className="mt-8" data-testid="learn-404-suggestions">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-bl-muted mb-3">
                                Did you mean…
                            </div>
                            <div className="space-y-2">
                                {suggestions.map((s) => (
                                    <Link
                                        key={s.slug}
                                        to={`/learn/${s.slug}`}
                                        className="bl-card p-4 block hover:border-bl-primary transition-colors"
                                        data-testid={`learn-404-suggestion-${s.slug}`}
                                    >
                                        <div className="font-display font-semibold text-bl text-base">
                                            {s.title}
                                        </div>
                                        {s.excerpt && (
                                            <div className="mt-1 text-sm text-bl-muted leading-relaxed line-clamp-2">
                                                {s.excerpt}
                                            </div>
                                        )}
                                        {s.category && (
                                            <div className="mt-2 text-[10px] uppercase tracking-[0.16em] text-bl-muted">
                                                {s.category}
                                            </div>
                                        )}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-8 flex gap-3">
                        <Link to="/learn" className="bl-btn-secondary" data-testid="learn-404-back">
                            Browse all articles
                        </Link>
                        <Link to="/" className="bl-btn-ghost" data-testid="learn-404-home">
                            Home
                        </Link>
                    </div>
                </div>
            </PublicLayout>
        );
    }

    const a = data.article;
    // ── Resolve structured fields. Content-engine articles store rich fields;
    // legacy CMS articles only have body markdown. We render whichever exist.
    const blocks = renderMarkdown(a.body);
    const takeaways = a.key_takeaways || [];
    const inspectionWording = a.inspection_wording_explained;
    const severityMeter = a.severity_meter;
    const questionsSeller = a.questions_to_ask_seller;
    const questionsInspector = a.questions_to_ask_inspector;
    const questionsContractor = a.questions_to_ask_contractor;
    const negotiationRelevance = a.negotiation_relevance;
    const repairExposureContext = a.repair_exposure_context;
    const whenToCallProfessional = a.when_to_call_professional;
    const realWorldScenario = a.real_world_scenario;
    const faq = a.faq_json || [];
    const ctaBlocks = a.cta_blocks_json || [];
    const endCta = ctaBlocks.find((c) => c.placement === "end") || ctaBlocks[0];
    const internalLinks = (a.internal_links || {}).links || [];
    const infographic = a.infographic_blueprint;
    const miniTool = a.mini_tool_config;
    const disclaimer = a.disclaimer;
    // Variant override: allow A/B test to swap the end CTA copy
    const finalEndCta =
        ctaVariant?.config?.end_cta && typeof ctaVariant.config.end_cta === "object"
            ? { ...endCta, ...ctaVariant.config.end_cta }
            : endCta;

    return (
        <PublicLayout>
            <article
                className="bl-container py-12 md:py-16 max-w-3xl"
                data-testid="learn-article"
            >
                {/* Hero */}
                <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted flex items-center gap-3" data-reveal="slide-left">
                    <span>{a.category}</span>
                    {a.reading_time ? (
                        <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {a.reading_time} min read
                        </span>
                    ) : null}
                    {a.published_at && (
                        <span>· Updated {String(a.published_at).slice(0, 10)}</span>
                    )}
                </div>
                <h1 className="mt-3 font-display font-semibold text-3xl md:text-5xl tracking-tight leading-[1.05] text-bl" data-reveal="slide-left" data-reveal-delay="1">
                    {a.title}
                </h1>
                {a.excerpt && (
                    <p className="mt-5 text-bl-muted text-lg leading-relaxed" data-reveal="slide-left" data-reveal-delay="2">
                        {a.excerpt}
                    </p>
                )}

                {/* Claymation hero image — present on packages generated via
                    the new image generator. Falls back to SVG infographic below. */}
                {a.hero_image?.url && (
                    <div
                        className="mt-7 rounded-xl overflow-hidden border border-bl-line"
                        data-reveal="fade"
                    >
                        <ParallaxImage
                            src={
                                a.hero_image.url.startsWith("http")
                                    ? a.hero_image.url
                                    : `${process.env.REACT_APP_BACKEND_URL}${a.hero_image.url}?w=1200`
                            }
                            alt={a.title}
                        />
                    </div>
                )}

                <KeyTakeaways items={takeaways} />

                {infographic && !a.hero_image?.url && (
                    <div data-reveal="fade">
                        <InfographicInline slug={slug} altText={infographic.alt_text} />
                    </div>
                )}

                {/* Main body */}
                <div className="mt-10 space-y-5 text-bl text-[17px] leading-[1.7]">
                    {blocks.map((b, i) => {
                        if (b.type === "h2")
                            return (
                                <h2
                                    key={i}
                                    className="mt-10 font-display font-semibold text-2xl md:text-3xl text-bl"
                                >
                                    {b.text}
                                </h2>
                            );
                        if (b.type === "h3")
                            return (
                                <h3
                                    key={i}
                                    className="mt-6 font-display font-semibold text-xl text-bl"
                                >
                                    {b.text}
                                </h3>
                            );
                        if (b.type === "hr")
                            return <div key={i} className="bl-divider my-8" />;
                        if (b.type === "ul")
                            return (
                                <ul key={i} className="space-y-2 pl-4">
                                    {b.items.map((it, j) => (
                                        <li key={j} className="flex gap-2.5">
                                            <span className="text-bl-primary mt-1">·</span>
                                            <span>{inlineMd(it)}</span>
                                        </li>
                                    ))}
                                </ul>
                            );
                        return <p key={i}>{inlineMd(b.text)}</p>;
                    })}
                </div>

                <InspectionWordingExplained items={inspectionWording} />
                <SeverityMeter meter={severityMeter} />

                {miniTool && (
                    <section className="mt-12" data-testid="learn-mini-tool-section">
                        <InteractiveMiniTool config={miniTool} articleSlug={slug} />
                    </section>
                )}

                {(questionsSeller || questionsInspector || questionsContractor) && (
                    <section className="mt-12" data-testid="learn-questions" data-reveal>
                        <h2 className="font-display font-semibold text-2xl md:text-3xl text-bl mb-4">
                            Questions to ask
                        </h2>
                        <div className="grid md:grid-cols-3 gap-3">
                            <QuestionList
                                heading="The seller"
                                icon={MessageCircle}
                                items={questionsSeller}
                                testid="q-seller"
                            />
                            <QuestionList
                                heading="The inspector"
                                icon={MessageCircle}
                                items={questionsInspector}
                                testid="q-inspector"
                            />
                            <QuestionList
                                heading="The contractor"
                                icon={Wrench}
                                items={questionsContractor}
                                testid="q-contractor"
                            />
                        </div>
                    </section>
                )}

                {negotiationRelevance && (
                    <section className="mt-12" data-testid="learn-negotiation" data-reveal>
                        <h2 className="font-display font-semibold text-2xl md:text-3xl text-bl mb-3">
                            How this affects negotiation
                        </h2>
                        <div className="text-bl leading-relaxed whitespace-pre-wrap">
                            {negotiationRelevance}
                        </div>
                    </section>
                )}

                {repairExposureContext && (
                    <section className="mt-10" data-testid="learn-exposure" data-reveal>
                        <h2 className="font-display font-semibold text-2xl md:text-3xl text-bl mb-3">
                            Repair exposure context
                        </h2>
                        <div className="text-bl leading-relaxed whitespace-pre-wrap">
                            {repairExposureContext}
                        </div>
                    </section>
                )}

                {whenToCallProfessional && (
                    <section className="mt-10" data-testid="learn-when-professional" data-reveal>
                        <div className="bl-card p-5 border-l-4 border-[var(--bl-amber)] flex gap-3">
                            <AlertTriangle className="h-5 w-5 text-[var(--bl-amber)] mt-0.5 shrink-0" />
                            <div>
                                <h3 className="font-display font-semibold text-lg text-bl mb-1">
                                    When to call a professional
                                </h3>
                                <p className="text-bl">{whenToCallProfessional}</p>
                            </div>
                        </div>
                    </section>
                )}

                {realWorldScenario && (
                    <section className="mt-10" data-testid="learn-scenario" data-reveal>
                        <div className="bl-card p-5 bg-bl-bg flex gap-3">
                            <Lightbulb className="h-5 w-5 text-bl-primary mt-0.5 shrink-0" />
                            <div>
                                <h3 className="font-display font-semibold text-lg text-bl mb-2">
                                    Real-world scenario
                                </h3>
                                <p className="text-bl whitespace-pre-wrap">{realWorldScenario}</p>
                            </div>
                        </div>
                    </section>
                )}

                {(endCta || ctaBlocks.length > 0) && (
                    <MainCtaBlock
                        block={finalEndCta}
                        articleSlug={slug}
                        variant={ctaVariant}
                    />
                )}

                <InternalLinksBlock links={internalLinks} />
                <FAQAccordion items={faq} />
                <ShareButtons slug={slug} title={a.title} />

                {disclaimer && (
                    <p
                        className="mt-12 text-xs text-bl-muted italic border-t border-bl/30 pt-6"
                        data-testid="learn-disclaimer"
                    >
                        {disclaimer}
                    </p>
                )}

                {/* Newsletter inline capture — sits between body and Related */}
                <NewsletterInlineCapture source={`learn_article:${slug}`} />

                {/* Related */}
                {data.related?.length > 0 && (
                    <div className="mt-16">
                        <div className="bl-eyebrow">Related reading</div>
                        <div className="mt-5 grid md:grid-cols-3 gap-4">
                            {data.related.map((r) => (
                                <Link
                                    key={r.slug}
                                    to={`/learn/${r.slug}`}
                                    className="bl-card bl-card-hover p-5 block"
                                    data-testid={`related-${r.slug}`}
                                >
                                    <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                                        {r.category}
                                    </div>
                                    <h4 className="mt-2 font-display font-semibold text-bl">
                                        {r.title}
                                    </h4>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </article>
        </PublicLayout>
    );
}
